import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { Col, Row, Spin, Select as AntdSelect } from 'antd';
import { cloneDeep, debounce, trim } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';

import Modal from "../../components/uiComponent/modal/index";
import Input from '../../components/uiComponent/inputs/labelInput';
import Button from "../../components/uiComponent/button/index";
import Select from "../../components/uiComponent/select/index";
import deleteIcon from "../../assets/images/newImages/delete-outlined.svg";

import InventoryWrapper from './style';
import { AddSku, GetAsinsList, clearField } from '../../slices/3-layer-products';
import {
  ITEM_CONDITIONS,
  IDENTIFIER_TYPES,
  VALID_UPC_REGEX,
  VALID_EAN_REGEX,
  VALID_ISBN_REGEX
} from '../../constants/threeLayer';

const { Option } = AntdSelect;

const initialProductData = {
  identifierType: '',
  identifier: '',
  packQuantity: '',
};

const initialSkuData = {
  asin: '',
  seller_sku: '',
  price: '',
  itemCondition: '',
  products: [
    {
      ...initialProductData
    }
  ]
};

const AddSkuModal = ({ open, onHide, onSave }) => {
  const dispatch = useDispatch();

  const [mappedAsinsList, setMappedAsinsList] = useState([]);
  const [skuData, setSkuData] = useState(initialSkuData);
  const [page, setPage] = useState({ pageSize: 50, pageNumber: 1 });
  const [filters, setFilters] = useState({ search: '' });

  const {
    asinsList,
    fetchingAsinsList,
    totalAsinsListCount,
    addingSku
  } = useSelector((state) => state.threeLayerProducts || {});

  useEffect(() => {
    dispatch(GetAsinsList({ page, filters }));

    return () => {
      dispatch(clearField({
        field: 'asinsList'
      }));
    }
  }, []);

  useEffect(() => {
    setMappedAsinsList(
      asinsList.map((asin) => ({ value: asin, label: asin, name: asin }))
    );
  }, [asinsList]);

  const handleAddField = () => {
    setSkuData(skuData => ({
      ...skuData, products: [...skuData.products, { ...initialProductData }]
    }));
  };

  const handleFieldChange = (index, field, value) => {
    setSkuData(
      skuData => {
        const skuDataClone = cloneDeep(skuData);
        skuDataClone.products[index][field] = value;
        return skuDataClone;
      }
    );
  };

  const handleRemoveField = (index) => {
    const skuDataClone = cloneDeep(skuData);
    const updatedFields = skuDataClone.products.filter((_, i) => i !== index);
    setSkuData({ ...skuData, products: updatedFields });
  };

  const createBtnDisableStatus = () => {
    const { asin, seller_sku, price, itemCondition, products } = skuData || {};
    if (!asin) return true;
    if (!seller_sku) return true;
    if (!price) return true;
    if (!itemCondition) return true;

    let isInvalidField = false;
    for (let i = 0; i < products.length; i++) {
      const {
        identifierType,
        identifier,
        packQuantity
      } = products[i];

      if (!identifierType || !identifier || !packQuantity || packQuantity < 1) {
        isInvalidField = true;
        break;
      }
      if (
        (identifierType === 'UPC' && !VALID_UPC_REGEX.test(identifier)) ||
        (identifierType === 'EAN' && !VALID_EAN_REGEX.test(identifier)) ||
        (identifierType === 'ISBN' && !VALID_ISBN_REGEX.test(identifier))
      ) {
        isInvalidField = true;
        break;
      }
    }
    return isInvalidField;
  };

  const handleScroll = (event) => {
    const { target } = event;

    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight &&
      !addingSku &&
      totalAsinsListCount !== asinsList.length
    ) {
      const newPage = { ...page, pageNumber: page.pageNumber + 1 };
      setPage(newPage);
      dispatch(GetAsinsList({
        page: newPage,
        filters,
        saveData: true
      }));
    }
  };

  const handleAsinSearch = debounce((value) => {
    if (trim(value) || filters.search) {
      setFilters({ search: value });
      setPage(page => ({ ...page, pageNumber: 1 }));
      dispatch(GetAsinsList({
        page: { ...page, pageNumber: 1 },
        filters: { search: value }
      }));
    }
  }, 800);

  return (
    <Modal
      open={open}
      onHide={onHide}
      closeText="Cancel"
      saveText="Create"
      header={true}
      closeButton={true}
      onClose={onHide}
      onSave={() => {
        dispatch(AddSku(skuData)).then(({ payload }) => {
          if (payload?.success) {
            onSave();
            onHide();
          }
        });
      }}
      buttonDisabled={createBtnDisableStatus()}
    >
      <InventoryWrapper className='sku-modal'>
        <Spin tip='Loading...' spinning={addingSku}>
          <h4 className='fw-bold mb-3'>SKU</h4>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3} sx={{ margin: '30px 0px 24px' }}>
            <Select
              isClearable={true}
              placeholder="ASIN"
              className="mb-0"
              label="ASIN"
              loading={fetchingAsinsList}
              options={mappedAsinsList}
              onInputChange={handleAsinSearch}
              onPopupScroll={handleScroll}
              onChange={(e) => setSkuData(skuData => ({ ...skuData, asin: e?.value }))}
            />
            <Input
              placeholder="XA-1X6U-FAHL"
              label="Seller SKU"
              marginBottom="0"
              onChange={({ target }) => setSkuData(skuData => ({ ...skuData, seller_sku: target?.value }))}
            />
            <Input
              placeholder="$456"
              label="Your Price"
              type="number"
              marginBottom="0"
              onChange={({ target }) => setSkuData(skuData => ({ ...skuData, price: target?.value }))}
            />
            <Select
              isClearable={true}
              placeholder="New Item"
              className="mb-0"
              label="Item Condition"
              options={ITEM_CONDITIONS}
              onChange={(e) => setSkuData(skuData => ({ ...skuData, itemCondition: e?.value }))}
            />
          </Box>
          <h4 className='fw-bold mb-3'>Product</h4>
          <div className='inputs-height'>
            {skuData.products.map((field, index) => (
              <Row gutter={[8, 12]} key={index}>
                <Col span={7}>
                  <Select
                    isClearable={true}
                    placeholder="Type"
                    className="mb-0"
                    label="Identifier Type"
                    options={IDENTIFIER_TYPES}
                    onChange={(e) => handleFieldChange(index, "identifierType", e?.value)}
                  />
                </Col>
                <Col span={7}>
                  <Input
                    placeholder="Identifier"
                    label="Identifier"
                    marginBottom="0"
                    onChange={(e) => handleFieldChange(index, "identifier", e.target.value)}
                  />
                </Col>
                <Col span={7}>
                  <Input
                    placeholder="Quantity"
                    label="Pack Quantity"
                    type="number"
                    marginBottom="0"
                    onChange={(e) => handleFieldChange(index, "packQuantity", e.target.value)}
                  />
                </Col>
                <Col span={3}>
                  <div className='add-btn d-flex'>
                    {index === 0 ? (
                      <Button
                        text="+"
                        className="outlined mb-0"
                        minWidth="100%"
                        onClick={handleAddField}
                      />
                    ) : (
                      <Button
                        className="outlined mb-0 delete-btn"
                        minWidth="100%"
                        startIcon={<img src={deleteIcon} alt='delete' />}
                        onClick={() => handleRemoveField(index)}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            ))}
          </div>
        </Spin>
      </InventoryWrapper>
    </Modal>
  );
};

export default AddSkuModal;
